import { Capacitor } from '@capacitor/core';

import { Provider } from '../model/tokens';

/**
 * The Grant that has to survive a redirect, on the web build only.
 *
 * A browser cannot hold a Grant in memory across federated sign-in: the authorization request
 * navigates the tab away and the response arrives in a **new page**, with the verifier and nonce
 * gone. RFC 7636 defines the transaction and says nothing about where a client keeps the verifier
 * between the two legs, so this is the browser-app answer to it — the same one Amplify,
 * oidc-client-ts, MSAL and every other browser library takes.
 *
 * ## What this knowingly gives up
 *
 * A code verifier in web storage, which native never does. Bounded rather than dismissed:
 *
 * - **`sessionStorage`, not `localStorage`** — scoped to the one tab doing the sign-in, and gone when
 *   that tab closes. `localStorage` would leave it readable by every other tab on the origin and
 *   would survive a browser restart, for no gain: the redirect returns to the same tab.
 * - **Single-use.** {@link takePendingAuthorization} removes it as it reads, so a callback URL that
 *   is reloaded, bookmarked or pasted somewhere cannot be redeemed a second time.
 * - **Web only.** {@link rememberPendingAuthorization} refuses to write on a native platform, so no
 *   code path can put a verifier in web storage on a device. The check lives here rather than at the
 *   call site, so there is one place it can be got wrong.
 *
 * This is the weaker half of a build that already keeps the Durable Root in `localStorage` because a
 * browser has no Keychain — accepted for an internal-only build in ADR 0003. The web build exists as
 * a development convenience; the production experience is a different application.
 */
export interface PendingAuthorization {
  readonly provider: Provider;
  /** Absent for Microsoft, whose code the Resource Server redeems. See `GrantInProgress`. */
  readonly codeVerifier?: string;
  /** The `state` nonce the request was made with, to compare the response against. */
  readonly nonce: string;
  /** When the request was sent, so the freshness bound is not measured from the new page's start. */
  readonly issuedAt: number;
}

const STORAGE_KEY = 'ninety.auth.pendingAuthorization';

/**
 * Remember a Grant for the length of a redirect. A no-op on native, where nothing is ever persisted.
 *
 * A failure to write is swallowed: `sessionStorage` throws in a browser with storage disabled, and
 * the honest consequence is a sign-in that cannot complete — which the machine already reports when
 * the resume never arrives. Throwing here would turn it into an unclassified Command failure instead.
 */
export function rememberPendingAuthorization(pending: PendingAuthorization): void {
  if (Capacitor.isNativePlatform()) return;

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
  } catch {
    // Storage unavailable. Nothing to do about it here, and nothing worth failing a Grant over.
  }
}

/**
 * Read the pending Grant and remove it, or answer `null`.
 *
 * Removing as it reads is what makes a callback single-use, and it happens before the value is even
 * parsed — so a stored value this cannot understand is also cleared rather than left to fail every
 * launch afterwards.
 */
export function takePendingAuthorization(): PendingAuthorization | null {
  let stored: string | null;
  try {
    stored = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    return null;
  }

  if (!stored) return null;

  try {
    return asPendingAuthorization(JSON.parse(stored) as unknown);
  } catch {
    return null;
  }
}

/** Every field, checked. A partial Grant would fail later and less legibly than refusing it here. */
function asPendingAuthorization(value: unknown): PendingAuthorization | null {
  const pending = (value ?? {}) as Record<string, unknown>;
  const { provider, codeVerifier, nonce, issuedAt } = pending;

  const usable =
    (provider === 'google' || provider === 'microsoft') &&
    typeof nonce === 'string' &&
    nonce.length > 0 &&
    typeof issuedAt === 'number' &&
    Number.isFinite(issuedAt);

  if (!usable) return null;

  /**
   * The verifier, held to the rule its own leg is held to.
   *
   * Required of a leg that redeems its own code, and absent from the one that does not — because
   * `transition()` reads exactly this to decide which of the two a resumed Grant is. A stored
   * Microsoft Grant that arrived carrying a verifier would be sent to Cognito's token endpoint with
   * an Azure code, and a stored Google Grant that lost one would be sent to the Resource Server as
   * though it were Microsoft's. Neither is a callback worth resuming, so both are refused.
   */
  if (provider === 'microsoft') {
    return codeVerifier === undefined ? { provider, nonce, issuedAt } : null;
  }

  return typeof codeVerifier === 'string' && codeVerifier.length > 0
    ? { provider, codeVerifier, nonce, issuedAt }
    : null;
}
